import { ECountries } from 'constants/enums';
import dayjs, { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import {
  ContractAdjustmentPayloadDtoAdjustmentTypeEnum,
  ContractResponseDtoContractStatusEnum,
} from 'libs/pinnacle-sdk';
import { isNil, uniq } from 'lodash';

export const cn = (...classes: (string | boolean | undefined)[]): string =>
  classes.filter(Boolean).join(' ');

export const realNumberToPercentage = (value: string | number) =>
  parseFloat(value.toString()) * 100;

export const isNumeric = (str: string) => {
  return !isNaN(parseFloat(str));
};

export const isStrictEmail = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const strictEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return strictEmailRegex.test(email);
};

export const simpleSearch = (value: string, search: string) => {
  return value.toLowerCase().includes(search.toLowerCase());
};

export const isUuid = (value: string | null | undefined): boolean => {
  if (!value) return false;
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
};

export const getUnderwritingYearsOptions = () => {
  return Array.from({ length: 8 }, (_, i) => {
    const year = dayjs().year() + 1 - i;
    return {
      label: year,
      value: year,
    };
  });
};

export const getDynamicTableHeight = (baseHeight = 500, bias = 0): number => {
  const screenHeight = window.innerHeight;
  // Based on 13 inch screen screen size
  // This serves as our reference screen size for proportional scaling
  const baseScreenHeight = 857;
  const availableHeight = screenHeight - bias;
  const availableBaseHeight = baseScreenHeight - bias;
  const ratio = baseHeight / availableBaseHeight;
  // Ensure minimum height of 100px to prevent tables from becoming unusable on very small screens
  return Math.max(100, Math.floor(availableHeight * ratio));
};

export const extractReceiptIdFromReceiptNo = (receiptNo: string) =>
  parseInt(receiptNo.split('-')[1]).toString();

export const getGstRateOnPremiumByBrokerCountry = (brokerCountry?: string) => {
  return brokerCountry === ECountries.AUSTRALIA ? 10 : 0;
};

export const getGstRateOnCedingCommissionByReinsuredCountry = (
  reinsuredCountry?: string
) => {
  return reinsuredCountry === ECountries.AUSTRALIA ? 10 : 0;
};

export const getContractStatusLabel =
  (t: TFunction) =>
  (
    status:
      | ContractResponseDtoContractStatusEnum
      | ContractAdjustmentPayloadDtoAdjustmentTypeEnum
  ) => {
    if (
      status === ContractAdjustmentPayloadDtoAdjustmentTypeEnum.Cancellation ||
      status === ContractResponseDtoContractStatusEnum.Cancelled
    ) {
      return t('contract.status.cancelled');
    }
    return status;
  };

export function uniqAndFilterNil<T>(array: (T | null | undefined)[]): T[] {
  return uniq(array).filter((item) => !isNil(item)) as T[];
}

export function getLocalDateForMidnightUTC(utcDate: Date) {
  const dt = dayjs.utc(utcDate);

  const utcHour = dt.hour();

  // find offset (timezone difference) where local time becomes 00:00
  let offset = (24 - utcHour) % 24;
  if (offset > 12) offset -= 24; // normalize into [-12, +14]

  // shift UTC time into that timezone → gives local midnight
  const localMidnight = dt.add(offset, 'hour');

  // return the DATE in that timezone
  return dayjs(localMidnight.format('YYYY-MM-DD'));
}
